import { HoldingWithApartment } from "@boark/shared/types/holding-with-apartment";
import { formatResidencyName } from "@boark/shared/utils/apartment-residency";
import { Outlet, useSearchParams } from "react-router-dom";

import { ApartmentLink } from "../../components/apartment-link";
import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { ResidencyName } from "../../components/residency-name";
import { Column, Filter, Table } from "../../components/table";
import { useGetApartments } from "../../queries/get-apartments";
import { useGetHoldings } from "../../queries/holding";
import { formatApartment, formatApartmentLabel } from "../../services/apartment";
import { useLocale } from "../../stores/locale";
import {
  compareContractLength,
  formatContractLength,
} from "../../utils/contract-length";
import { createIssueRouteForRecord } from "../../utils/create-issue-route";
import { formatDate } from "../../utils/date";
import { isCurrent } from "../../utils/is-current";
import { recordIdToId } from "../../utils/record-id-to-id";

type HoldingWithApartmentData = HoldingWithApartment & {
  active: boolean;
};

export function Holdings() {
  const { locale: t, get: getLocale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: holdings, isLoading: isHoldingsLoading } = useGetHoldings();
  const { data: apartments } = useGetApartments();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.holding.holdings}</H1>
          <Button route="/admin/register/holding/create">
            {t.holding.create}
          </Button>
        </div>
        <Table
          actions={(row) => [
            {
              label: getLocale("holding.viewApartment"),
              route: `/admin/register/apartment/?filterBy=${encodeURIComponent(
                JSON.stringify([
                  {
                    field: "index",
                    value: row.apartment.index,
                    operator: "eq",
                  },
                ]),
              )}`,
            },
            {
              label: t.issue.create,
              route: createIssueRouteForRecord({
                type: "holding",
                recordId: recordIdToId(row.id),
                label: `${formatApartmentLabel(row.apartment)} — ${formatResidencyName(row)} (${formatDate(row.fromDate)})`,
              }),
            },
          ]}
          data={(holdings ?? []).map((holding) => ({
            ...holding,
            active: isCurrent(holding),
          }))}
          editRouteButton={(row) =>
            `/admin/register/holding/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          loading={isHoldingsLoading}
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/register/holding/${recordIdToId(row.id)}/delete`
          }
          sortBy={searchParams.get("sortBy") || "apartment.index"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({
              filterBy: JSON.stringify(value),
            });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column
            body={(row: HoldingWithApartmentData) => (
              <ApartmentLink apartment={row.apartment} />
            )}
            field="apartment.index"
            filterableBy="select"
            filterOptions={(data: HoldingWithApartmentData[]) =>
              apartments
                ?.filter((apartment) =>
                  data.some(
                    (holding) => holding.apartment.id === apartment.id,
                  ),
                )
                .map((apartment) => ({
                  label: formatApartment(apartment),
                  value: apartment.index,
                })) || []
            }
          >
            {t.apartment.apartment}
          </Column>
          <Column
            body={(row: HoldingWithApartmentData) => (
              <ResidencyName residency={row} />
            )}
            field="owners"
            sortCallback={(
              a: HoldingWithApartmentData,
              b: HoldingWithApartmentData,
              _field: string,
              direction: "asc" | "desc",
            ) => {
              const result = formatResidencyName(a).localeCompare(
                formatResidencyName(b),
              );
              if (direction === "asc") {
                return result;
              }
              return -result;
            }}
          >
            {getLocale("holding.residents")}
          </Column>
          <Column
            body={(row: HoldingWithApartmentData) => formatDate(row.fromDate)}
            field="fromDate"
            filterableBy="date"
          >
            {getLocale("holding.fromDate")}
          </Column>
          <Column
            body={(row: HoldingWithApartmentData) =>
              (row.toDate && formatDate(row.toDate)) ?? "-"
            }
            field="toDate"
            filterableBy="date"
          >
            {getLocale("holding.toDate")}
          </Column>
          <Column
            body={(row: HoldingWithApartmentData) =>
              formatContractLength(row.fromDate, row.toDate, {
                year: t.year,
                years: t.years,
                month: t.month,
                months: t.months,
              })
            }
            field="contractLength"
            sortCallback={(
              a: HoldingWithApartmentData,
              b: HoldingWithApartmentData,
              _field: string,
              direction: "asc" | "desc",
            ) => compareContractLength(a, b, direction)}
          >
            {getLocale("holding.contractLength")}
          </Column>
          <Column
            body={(row: HoldingWithApartmentData) =>
              row.active ? t.yes : t.no
            }
            field="active"
            filterableBy="select"
            filterOptions={() => [
              { label: t.yes, value: true },
              { label: t.no, value: false },
            ]}
          >
            {getLocale("holding.active")}
          </Column>
          <Column field="issueCount">{t.holding.issueCount}</Column>
        </Table>
      </div>
    </>
  );
}
